import { Injectable } from "@angular/core";
import { RemoteDataClientService, RmtPropertySetterValue } from "./remote-data-client.service";
import { AuthService } from "acl-cloud-lib";
import { Observable } from "rxjs";
//import { RmtPropertyUpdates, SeriesUpdateData } from "src/api/generated";
import { AvailableSeriesData, SeriesUpdateData } from "./model";


/**
 * Wrapper service for working with hub clients.
 *
 * @export
 * @class RemoteDataWrapperService
 */
@Injectable({ providedIn: 'root' })
export class RemoteDataWrapperService {

    /**
     * Map of available clients. Each machine has own client.
     *
     * @private
     * @type {Map<string, RemoteDataClientService>}
     * @memberof RemoteDataWrapperService
     */
    private _availableClients: Map<string, RemoteDataClientService> = new Map();

    constructor(private readonly _authService: AuthService) {
    }

    /**
     * Get client from map, if does not exist, create new one. 
     *
     * @param {string} machine
     * @return {*}  {RemoteDataClientService}
     * @memberof RemoteDataWrapperService
     */
    public getClient(machine: string): RemoteDataClientService {
        if (this._availableClients.has(machine)) {
            const service: RemoteDataClientService | undefined = this._availableClients.get(machine);;

            if (service === undefined) {
                return this._initializeNewService(machine);
            }

            return service;
        } else {
            return this._initializeNewService(machine);
        }
    }


    /**
     * Request available series for machine.
     *
     * @param {string} machine
     * @return {*}  {Observable<AvailableSeriesResponse>}
     * @memberof RemoteDataWrapperService
     */
    public getAvailableSeries(machine: string): Observable<AvailableSeriesData> {
        if (this._availableClients.has(machine)) {
            return this._availableClients.get(machine)!.availableSeries$;
        } else {
            const client: RemoteDataClientService = this._initializeNewService(machine);
            return client.availableSeries$;
        }
    }

    /**
     * Request data for chart.
     *
     * @param {string} machine
     * @param {string} series
     * @return {*}  {Observable<IRemoteData>}
     * @memberof RemoteDataWrapperService
     */
    public getData(machine: string, series: string): Observable<SeriesUpdateData> {
        const client: RemoteDataClientService = this.getClient(machine);
        client.dataRequest(series);

        return client.getDataSubject(series);
    }

    /**
     * Starts or ends remote data updates subscription.
     *
     * @param {string} machine
     * @param {boolean} watch
     * @memberof RemoteDataWrapperService
     */
    public watchRemoteDataUpdates(machine: string, watch: boolean): void {
        const client: RemoteDataClientService = this.getClient(machine);
        client.watchRemoteDataUpdates(watch);
    }

    /**
     * Parsed machine structure observable.
     *
     * @param {string} machineName
     * @return {*}  {(Observable<RealObjectStructure | null>)}
     * @memberof RemoteDataWrapperService
     */
    public getMachineStructure(machineName: string): Observable<any | null> {
        const client: RemoteDataClientService = this.getClient(machineName);
        return client.getMachineOne().machineStructure$;
    }

    /**
     * Raw json of machine structure.
     *
     * @param {string} machineName
     * @return {*}  {Observable<string>}
     * @memberof RemoteDataWrapperService
     */
    public getMachineStructureRaw(machineName: string): Observable<string> {
        const client: RemoteDataClientService = this.getClient(machineName);
        return client.getMachineOne().machineStructureRaw$;
    }

    /**
     * Remove all empty clients from map.
     *
     * @memberof RemoteDataWrapperService
     */
    public clearUnusedConnections(): void {
        this._availableClients.forEach(val => {
            if (val.disconnectIfNotUsed()) {
                this._availableClients.delete(val.currentMachineName);
            }
        });
    }

    /**
     * Disconnect all clients.
     *
     * @memberof RemoteDataWrapperService
     */
    public hardDisconnectAll(): void {
        this._availableClients.forEach(val => {
            val.disconnect();
        });

        this._availableClients.clear();
    }

    /**
     * Signal machine to exit room and stop sending data, then disconnect client
     *
     * @param {string} machineName
     * @return {*}  {Promise<void>}
     * @memberof RemoteDataWrapperService
     */
    public async disconnectMachine(machineName: string): Promise<void> {
        if (this._availableClients.has(machineName)) {
            const client = this._availableClients.get(machineName);
            if (client) {
                // First, signal the machine to exit the room
                await client.exitRoom();

                // Then disconnect the client
                client.disconnect();

                // Remove from map
                this._availableClients.delete(machineName);
            }
        }
    }

    /**
     * Enable communication for all clients.
     *
     * @memberof RemoteDataWrapperService
     */
    public turnOnCommunication(): void {
        this._availableClients.forEach((client) => {
            client.turnOnCommunictaion();
        });
        //this.clearUnusedConnections();
    }

    /**
     * Returns supplier state observable.
     *
     * @param {string} machineName
     * @return {*}  {Observable<boolean>}
     * @memberof RemoteDataWrapperService
     */
    public getSupplierState(machineName: string): Observable<boolean> {
        return this.getClient(machineName).supplierState$;
    }

    /**
     * Disable communication for all clients.
     *
     * @memberof RemoteDataWrapperService
     */
    public turnOffCommunication(): void {
        this._availableClients.forEach((client) => {
            client.turnOffCommunication();
        });
    }

    /**
     * Invokes one of available methods on supplier.
     *
     * @param {string} machineName
     * @param {string} functionName
     * @param {(any[] | null)} args
     * @return {*}  {Promise<boolean>}
     * @memberof RemoteDataWrapperService
     */
    public invokeOnSupplier(machineName: string, functionName: string, args: any[] | null): Promise<boolean> {
        return this.getClient(machineName).invokeOnSupplier(functionName, args);
    }


    /**
     * Invokes one of available methods on supplier.
     *
     * @param {string} machineName
     * @param {number} id
     * @param {(any[] | null)} args
     * @return {*}  {Promise<boolean>}
     * @memberof RemoteDataWrapperService
     */
    public invokeOnSupplierById(machineName: string, id: number, args: any[] | null): Promise<boolean> {
        return this.getClient(machineName).invokeOnSupplierById(id, args);
    }

    /**
     * Sets property on supplier
     *
     * @param {string} machineName
     * @param {number} id
     * @param {RmtPropertySetterValue} value
     * @return {*}  {Promise<boolean>}
     * @memberof RemoteDataWrapperService
     */
    public setPropertyOnSupplier(machineName: string, id: number, value: RmtPropertySetterValue): Promise<boolean> {
        return this.getClient(machineName).setPropertyOnSupplier(id, value);
    }

    /**
     * New client builder.
     *
     * @private
     * @param {string} machine
     * @return {*}  {RemoteDataClientService}
     * @memberof RemoteDataWrapperService
     */
    private _initializeNewService(machine: string): RemoteDataClientService {
        const service: RemoteDataClientService = new RemoteDataClientService(this._authService);
        service.setCurrentMachineName(machine);
        this._availableClients.set(machine, service);
        return service;
    }
}